import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  responsiveDialogBodyClass,
} from "@/components/ui/ResponsiveDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, UserPlus, Mail, Loader2, ArrowLeft, Link2, Building2, Unlink } from "@/components/ds/icons";
import { PageHeader } from "@/components/ui/PageHeader";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { cleanRut, formatRut, validateRut } from "@/lib/rut";
import { RutInput } from "@/components/RutInput";

type EstadoRelacion = "pendiente" | "activa" | "rechazada";

interface Empresa {
  id: number;
  rut: string;
  nombre: string;
  estado: "pendiente" | "activa";
}

interface Mandante {
  relacion_id: number;
  estado_relacion: EstadoRelacion;
  empresa: Empresa;
  contacto_email: string | null;
  contratos_count: number | null;
}

const ESTADO_LABEL: Record<EstadoRelacion, string> = {
  pendiente: "Pendiente",
  activa: "Activa",
  rechazada: "Rechazada",
};

const ESTADO_VARIANT: Record<EstadoRelacion, "default" | "secondary" | "destructive" | "outline"> = {
  activa: "default",
  pendiente: "secondary",
  rechazada: "destructive",
};

export function Mandantes() {
  const [mandantes, setMandantes] = useState<Mandante[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [aDesvincular, setADesvincular] = useState<Mandante | null>(null);
  const [desvinculando, setDesvinculando] = useState(false);

  const desvincular = async () => {
    if (!aDesvincular) return;
    setDesvinculando(true);
    try {
      await api.delete(`/comercial/mandantes/${aDesvincular.empresa.id}`);
      toast.success(`${aDesvincular.empresa.nombre} fue desvinculada.`);
      setADesvincular(null);
      cargar();
    } catch {
      toast.error("No se pudo desvincular la empresa.");
    } finally {
      setDesvinculando(false);
    }
  };

  const cargar = async () => {
    setIsLoading(true);
    try {
      const res = await api.get<{ data: Mandante[] }>("/comercial/mandantes");
      setMandantes(res.data);
    } catch {
      toast.error("No se pudo cargar el listado de mandantes.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return mandantes;
    const rutQuery = cleanRut(q);
    return mandantes.filter(
      (c) =>
        c.empresa.nombre.toLowerCase().includes(q) ||
        (rutQuery !== "" && c.empresa.rut.toLowerCase().includes(rutQuery)) ||
        (c.contacto_email ?? "").toLowerCase().includes(q),
    );
  }, [mandantes, search]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Mandantes"
        subtitle="Empresas que te contratan y pueden auditar tus contratos y documentos."
        cta={{
          label: "Invitar Mandante",
          icon: UserPlus,
          onClick: () => setDialogOpen(true),
        }}
      />
      <InvitarMandanteDialog
        key={dialogOpen ? "open" : "closed"}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={() => {
          setDialogOpen(false);
          cargar();
        }}
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Mandantes vinculados</CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, RUT o email..."
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              {mandantes.length === 0
                ? "Aún no invitaste mandantes. Haz clic en 'Invitar Mandante' para empezar."
                : "No hay resultados para esa búsqueda."}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {filtered.map((c) => (
                <Card key={c.relacion_id}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <CardTitle className="text-lg truncate">{c.empresa.nombre}</CardTitle>
                        <CardDescription>{formatRut(c.empresa.rut)}</CardDescription>
                      </div>
                      <Badge variant={ESTADO_VARIANT[c.estado_relacion]}>
                        {ESTADO_LABEL[c.estado_relacion]}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {c.contacto_email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span className="truncate">{c.contacto_email}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <Badge variant="secondary">{c.contratos_count ?? 0} contratos</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setADesvincular(c)}
                      >
                        <Unlink className="h-4 w-4 mr-1" />
                        Desvincular
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={aDesvincular !== null} onOpenChange={(open) => !open && setADesvincular(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desvincular mandante</AlertDialogTitle>
            <AlertDialogDescription>
              {aDesvincular ? (
                <>
                  ¿Seguro que quieres desvincular a <strong>{aDesvincular.empresa.nombre}</strong>?
                  La empresa dejará de aparecer en tu listado.
                  {aDesvincular.contratos_count && aDesvincular.contratos_count > 0 ? (
                    <>
                      {" "}
                      Los <strong>{aDesvincular.contratos_count} contratos</strong> existentes
                      quedarán archivados como históricos.
                    </>
                  ) : null}{" "}
                  Si después la quieres volver a vincular, puedes hacerlo desde "Invitar Mandante".
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={desvinculando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                desvincular();
              }}
              disabled={desvinculando}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {desvinculando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Desvincular"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface Candidato {
  empresa_id: number;
  empresa_nombre: string;
  contacto_nombre: string;
}

interface BuscarResponse {
  tipo: "nuevo" | "existente" | "duplicado" | "auto" | "multiple";
  message?: string;
  empresa?: { id: number; nombre: string };
  contacto?: { nombre: string; email: string };
  candidatos?: Candidato[];
}

/**
 * `store`/`vincular` pueden devolver 422 con `{ candidatos: [...] }` (mismo
 * shape que `buscar` tipo 'multiple') cuando aparecen N owners entre la
 * verificación inicial y el envío. Se extrae de forma segura desde el body
 * crudo de la respuesta de error (`ApiError.data`).
 */
function extraerCandidatos(data: unknown): Candidato[] | undefined {
  if (!data || typeof data !== "object") return undefined;
  const candidatos = (data as { candidatos?: unknown }).candidatos;
  return Array.isArray(candidatos) ? (candidatos as Candidato[]) : undefined;
}

type Step = "email" | "nuevo" | "existente" | "multiple";

interface InvitarMandanteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

function InvitarMandanteDialog({ open, onOpenChange, onCreated }: InvitarMandanteDialogProps) {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [empresaExistente, setEmpresaExistente] = useState<{ nombre: string } | null>(null);
  const [contactoExistente, setContactoExistente] = useState<{ nombre: string } | null>(null);
  const [candidatos, setCandidatos] = useState<Candidato[]>([]);
  const [empresaIdSeleccionada, setEmpresaIdSeleccionada] = useState<number | null>(null);
  const [empresaRut, setEmpresaRut] = useState("");
  const [empresaNombre, setEmpresaNombre] = useState("");
  const [mandanteNombre, setMandanteNombre] = useState("");
  const [mandanteApellido, setMandanteApellido] = useState("");
  const [isVerificando, setIsVerificando] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRutError, setShowRutError] = useState(false);

  const verificar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error("Ingresa un correo.");
      return;
    }

    setIsVerificando(true);
    try {
      const res = await api.get<BuscarResponse>(
        `/comercial/mandantes/buscar?email=${encodeURIComponent(email)}`,
      );

      if (res.tipo === "nuevo") {
        setStep("nuevo");
      } else if (res.tipo === "existente" && res.empresa) {
        setEmpresaExistente(res.empresa);
        setContactoExistente(res.contacto ?? null);
        setStep("existente");
      } else if (res.tipo === "multiple" && res.candidatos && res.candidatos.length > 0) {
        setCandidatos(res.candidatos);
        setEmpresaIdSeleccionada(null);
        setStep("multiple");
      } else if (res.tipo === "duplicado" || res.tipo === "auto") {
        toast.error(res.message ?? "No es posible enviar la invitación.");
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.errors) {
        const firstError = Object.values(err.errors)[0]?.[0];
        toast.error(firstError ?? "Correo inválido.");
      } else {
        toast.error("No se pudo verificar el correo.");
      }
    } finally {
      setIsVerificando(false);
    }
  };

  const submitNuevo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!empresaRut || !empresaNombre || !mandanteNombre || !mandanteApellido) {
      toast.error("Completa todos los campos.");
      return;
    }

    if (!validateRut(empresaRut)) {
      setShowRutError(true);
      toast.error("El RUT de la empresa es inválido.");
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post("/comercial/mandantes", {
        empresa_rut: empresaRut,
        empresa_nombre: empresaNombre,
        mandante_nombre: mandanteNombre,
        mandante_apellido: mandanteApellido,
        mandante_email: email,
      });

      toast.success(`Se envió email de registro a ${email}.`);
      onCreated();
    } catch (err) {
      handleSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitVincular = async (empresaId?: number) => {
    setIsSubmitting(true);
    try {
      await api.post("/comercial/mandantes/vincular", {
        mandante_email: email,
        ...(empresaId ? { empresa_id: empresaId } : {}),
      });
      toast.success(`Se envió email de vinculación a ${email}.`);
      onCreated();
    } catch (err) {
      handleSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitError = (err: unknown) => {
    if (err instanceof ApiError && err.status === 422) {
      const candidatosDesambiguar = extraerCandidatos(err.data);
      if (candidatosDesambiguar && candidatosDesambiguar.length > 0) {
        setCandidatos(candidatosDesambiguar);
        setEmpresaIdSeleccionada(null);
        setStep("multiple");
        toast.error("Hay más de una cuenta con ese correo. Elige la empresa correcta.");
        return;
      }
      if (err.errors) {
        const firstError = Object.values(err.errors)[0]?.[0];
        toast.error(firstError ?? "Revisa los datos.");
        return;
      }
    }
    toast.error("No se pudo enviar la invitación.");
  };

  const volver = () => {
    setStep("email");
    setEmpresaExistente(null);
    setContactoExistente(null);
    setCandidatos([]);
    setEmpresaIdSeleccionada(null);
    setEmpresaRut("");
    setEmpresaNombre("");
    setMandanteNombre("");
    setMandanteApellido("");
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      dismissible={step === "email"}
    >
      <ResponsiveDialogContent className="sm:max-w-md">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>Invitar mandante</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {step === "email"
              ? "Ingresa el correo del mandante. Si ya tiene cuenta, te lo vinculamos directo."
              : step === "existente"
                ? "Este correo ya tiene una cuenta. Vamos a vincular tu empresa con la del mandante."
                : step === "multiple"
                  ? "Hay más de una cuenta registrada con ese correo. Elige la empresa correcta."
                  : "Carga los datos de la empresa mandante. Le vamos a enviar un email para que confirme su registro."}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        {step === "email" && (
          <form onSubmit={verificar} className={responsiveDialogBodyClass}>
            <div className="space-y-2">
              <Label htmlFor="email">Email del contacto</Label>
              <Input
                id="email"
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="contacto@empresa.cl"
                disabled={isVerificando}
              />
            </div>
            <ResponsiveDialogFooter>
              <Button type="submit" disabled={isVerificando} className="gap-2">
                {isVerificando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Verificar
              </Button>
            </ResponsiveDialogFooter>
          </form>
        )}

        {step === "existente" && empresaExistente && (
          <div className={responsiveDialogBodyClass}>
            <div className="rounded-lg border bg-muted/40 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <div className="min-w-0">
                  <div className="font-medium truncate">{empresaExistente.nombre}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="truncate">{email}</span>
                {contactoExistente && (
                  <span className="text-muted-foreground">— {contactoExistente.nombre}</span>
                )}
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Le vamos a enviar un email para que acepte la vinculación. Hasta que la acepte, la
              relación queda en estado pendiente.
            </p>
            <ResponsiveDialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={volver} disabled={isSubmitting}>
                <ArrowLeft className="h-4 w-4" />
                Atrás
              </Button>
              <Button
                type="button"
                onClick={() => submitVincular()}
                disabled={isSubmitting}
                className="gap-2"
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                Vincular
              </Button>
            </ResponsiveDialogFooter>
          </div>
        )}

        {step === "multiple" && (
          <div className={responsiveDialogBodyClass}>
            <p className="text-sm text-muted-foreground">
              Encontramos {candidatos.length} empresas registradas con el correo{" "}
              <strong>{email}</strong>. Elige la empresa correcta.
            </p>
            <RadioGroup
              value={empresaIdSeleccionada !== null ? String(empresaIdSeleccionada) : ""}
              onValueChange={(value) => setEmpresaIdSeleccionada(Number(value))}
            >
              {candidatos.map((c) => (
                <Label
                  key={c.empresa_id}
                  htmlFor={`mandante-candidato-${c.empresa_id}`}
                  className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-muted/40"
                >
                  <RadioGroupItem
                    value={String(c.empresa_id)}
                    id={`mandante-candidato-${c.empresa_id}`}
                  />
                  <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.empresa_nombre}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.contacto_nombre}</div>
                  </div>
                </Label>
              ))}
            </RadioGroup>
            <ResponsiveDialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={volver} disabled={isSubmitting}>
                <ArrowLeft className="h-4 w-4" />
                Atrás
              </Button>
              <Button
                type="button"
                onClick={() => submitVincular(empresaIdSeleccionada ?? undefined)}
                disabled={isSubmitting || empresaIdSeleccionada === null}
                className="gap-2"
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                Vincular
              </Button>
            </ResponsiveDialogFooter>
          </div>
        )}

        {step === "nuevo" && (
          <form onSubmit={submitNuevo} className={responsiveDialogBodyClass}>
            <div className="space-y-2">
              <Label>Email del contacto</Label>
              <Input value={email} disabled />
            </div>
            <div className="space-y-2">
              <Label htmlFor="empresa-rut">RUT empresa</Label>
              <RutInput
                id="empresa-rut"
                value={empresaRut}
                onChange={setEmpresaRut}
                placeholder="76.123.456-7"
                disabled={isSubmitting}
                validate
                showError={showRutError}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="empresa-nombre">Nombre empresa</Label>
              <Input
                id="empresa-nombre"
                value={empresaNombre}
                onChange={(e) => setEmpresaNombre(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="contacto-nombre">Nombre contacto</Label>
                <Input
                  id="contacto-nombre"
                  value={mandanteNombre}
                  onChange={(e) => setMandanteNombre(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contacto-apellido">Apellido</Label>
                <Input
                  id="contacto-apellido"
                  value={mandanteApellido}
                  onChange={(e) => setMandanteApellido(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <ResponsiveDialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={volver} disabled={isSubmitting}>
                <ArrowLeft className="h-4 w-4" />
                Atrás
              </Button>
              <Button type="submit" disabled={isSubmitting} className="gap-2">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                Enviar invitación
              </Button>
            </ResponsiveDialogFooter>
          </form>
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
